import { FC, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { FaDownload } from 'react-icons/fa';
import { buildClientEntity } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import API from 'core/Api';
import { useLanguageContext } from 'store/language';
import { formatDate } from 'utils/helpers/formatters';
import { useTranslation } from 'react-i18next';
import { useModal } from 'store/modal';
import { useSelectableTableContext } from 'store/selectable-table';
import { requestMultipleDownload, downloadMultipleFiles, extractErrorMessage } from 'utils/helpers/archives';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import InnerWrapper from 'routes/client/reports/InnerWrapper';
import ReportForm from './components/ReportForm';
import DocsTable from './components/DocsTable';

type ReportParams = Partial<{
  date_from: string;
  date_to: string;
  user_id: number[];
  creator_id: number[];
  group_id: number[];
  category_id: number[];
  action_id: number[];
}>;

const DocumentReport: FC = () => {
  const [reportParams, setReportParams] = useState<ReportParams | null>(null);
  const [isDownloadMode, setIsDownloadMode] = useState(false);
  const methods = useForm();
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();
  const { selectedRowsOnAction, resetSelectable, setShowSelectColumn, showSelectColumn } = useSelectableTableContext();

  useEffect(() => {
    resetSelectable();
    setShowSelectColumn(false);
  }, []);

  const {
    isLoading: isLoadingDropdowns,
    error: errorDropdowns,
    data: dropdowns,
  } = useQuery<any, Error>([buildClientEntity('report-document-actions', client)], (): Promise<any> => {
    return API.get('reports/document-actions/dropdown-options').then(res => {
      return res.data[language || 'bg'];
    });
  });

  const handleSubmitReport = (data: any) => {
    let dateFrom = data.date_from;
    let dateTo = data.date_to;

    // Check if any date is selected
    const shouldPopulateDates = !!dateFrom || !!dateTo;

    if (shouldPopulateDates) {
      dateFrom = data.date_from && formatDate(data.date_from, 'YYY-mm-dd hh:ii:ss');

      dateTo = data.date_to
        ? formatDate(data.date_to, 'YYY-mm-dd hh:ii:ss')
        : formatDate(new Date(), 'YYY-mm-dd hh:ii:ss');

      /**
       * In case any of the date fields (but not both) is empty on submit -
       * set value so that the time period is visible in filters
       */
      if (dateFrom) methods.setValue('date_from', new Date(dateFrom));
      if (dateTo) methods.setValue('date_to', new Date(dateTo));
    }

    const buildReportParams = (dataRes: any, dateFromCurr: string | null, dateToCurr: string | null) => {
      const entries: [string, any][] = [];

      if (dateFromCurr) entries.push(['date_from', dateFromCurr]);
      if (dateToCurr) entries.push(['date_to', dateToCurr]);

      const fields = [
        ['user_id', dataRes.user_id],
        ['creator_id', dataRes.creator_id],
        ['group_id', dataRes.group_id],
        ['category_id', dataRes.category_id],
        ['action_id', dataRes.action_id],
      ];

      fields.forEach(([key, value]) => {
        const values = value?.map((v: any) => v.value);
        if (values?.length) entries.push([key, values]);
      });

      return Object.fromEntries(entries);
    };

    const formattedData = buildReportParams(data, dateFrom, dateTo);
    setReportParams(formattedData);
  };

  const confirmBulkDownloadFlow = async (rowIds: number[]) => {
    if (rowIds.length === 0) {
      showModal({
        type: 'error',
        content: t('documents.table.noDocsSelected'),
      });
      return;
    }

    try {
      showModal({
        type: 'loading',
        heading: t('documents.bulkDownload.preparing'),
        content: t('documents.bulkDownload.pleaseWait'),
      });

      const response = await requestMultipleDownload(rowIds, 'reports-document-actions-tab');

      if (response.type === 'immediate-download') {
        hideModal();
        showModal({
          type: 'success',
          content: t('documents.bulkDownload.immediateDownloadMessage'),
        });

        downloadMultipleFiles(response.links);

        resetSelectable();
        setShowSelectColumn(false);
        setIsDownloadMode(false);
      } else {
        hideModal();
        showModal({
          type: 'success',
          heading: t('documents.bulkDownload.archiveGenerating'),
          content: t('documents.bulkDownload.archiveMessage'),
        });

        resetSelectable();
        setShowSelectColumn(false);
        setIsDownloadMode(false);
      }
    } catch (err: any) {
      hideModal();
      const errorMessage = extractErrorMessage(err, t('documents.bulkDownload.error'));
      showModal({
        type: 'error',
        content: errorMessage,
      });
    }
  };

  return (
    <InnerWrapper
      isLoading={isLoadingDropdowns}
      error={errorDropdowns}
    >
      {dropdowns && (
        <ReportForm
          data={dropdowns}
          methods={methods}
          onSubmit={handleSubmitReport}
          reportParams={reportParams}
        />
      )}
      {reportParams && (
        <div className="mt-3">
          {!showSelectColumn && (
            <div className="d-flex justify-content-center justify-content-sm-end mb-3">
              <TooltipWrapper
                id="tooltip-mass-download"
                overlay={t('documents.bulkDownload.tooltip')}
              >
                <Button
                  btnType="secondary"
                  onClick={() => {
                    setShowSelectColumn(true);
                    setIsDownloadMode(true);
                  }}
                  preventDebounce
                >
                  <FaDownload style={{ marginRight: '0.5rem' }} />
                  {t('documents.table.massDownload')}
                </Button>
              </TooltipWrapper>
            </div>
          )}
          {showSelectColumn && isDownloadMode && (
            <div className="d-flex justify-content-center justify-content-sm-end mb-3">
              <Button
                className="mr-2"
                btnType="secondary"
                onClick={() => selectedRowsOnAction(confirmBulkDownloadFlow)}
                preventDebounce
              >
                <FaDownload
                  size={14}
                  className="mr-2"
                />
                {t('documents.table.download')}
              </Button>
              <Button
                btnType="light"
                outlined
                onClick={() => {
                  resetSelectable();
                  setShowSelectColumn(false);
                  setIsDownloadMode(false);
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          )}
          <DocsTable
            reportParams={reportParams}
            isDownloadMode={isDownloadMode}
          />
        </div>
      )}
    </InnerWrapper>
  );
};

export default DocumentReport;
